import { useState, useCallback } from "react";
import { useFindMany } from "@gadgetinc/react";
import { Link } from "react-router";
import { api } from "../../api";
import LoadingSpinner from "../LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Badge } from "../tailwind/badge";
import { Button } from "../tailwind/button";
import { Tooltip as TooltipTailwind } from "../Tooltip";
import { Text } from "../tailwind/text";
import { ArrowLeftIcon } from "@heroicons/react/16/solid";
import { ArrowRightIcon } from "@heroicons/react/20/solid";

export function RecipientsList({ campaign, filterDates, isMobile }) {
  const NUM_ON_PAGE = 5;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  const [{ data, fetching }] = useFindMany(api.message, {
    ...cursor,
    filter: [
      {
        campaignId: { equals: campaign },
        direction: { equals: "outbound" },
      },
      {
        AND: [
          { createdAt: { greaterThan: filterDates.startDate } },
          { createdAt: { lessThan: undefined } },
        ],
      },
    ],
    select: {
      id: true,
      createdAt: true,
      sentAt: true,
      deliveredAt: true,
      readAt: true,
      failedAt: true,
      status: true,
      failedReason: true,
      conversation: {
        id: true,
        contact: {
          id: true,
          firstName: true,
          lastName: true,
          whatsappName: true,
          userPhoneNumber: true,
        },
      },
    },
    sort: {
      createdAt: "Descending",
    },
  });

  const getNextPage = useCallback(() => {
    setCursor({ first: NUM_ON_PAGE, after: data.endCursor });
    setPageNumber(pageNumber + 1);
  }, [data]);

  const getPreviousPage = useCallback(() => {
    setCursor({ last: NUM_ON_PAGE, before: data.startCursor });
    setPageNumber(pageNumber - 1);
  }, [data]);

  return (
    <>
      {fetching && <LoadingSpinner />}

      <div className="flex flex-col justify-between grow">
        {!fetching && (
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-[30%]">Sending date</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader className="w-[30%]">Contact name</TableHeader>
                  {!isMobile && <TableHeader>Phone number</TableHeader>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length > 0 ? (
                  data.map((message, index) => {
                    let messageStatus = message.readAt
                      ? "read"
                      : message.deliveredAt
                        ? "delivered"
                        : message.failedAt
                          ? "failed"
                          : message.sentAt
                            ? "sent"
                            : "failed";

                    return (
                      <TableRow
                        key={message.id}
                        id={message.id}
                        position={index}
                      >
                        <TableCell>
                          {new Date(message?.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                            },
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="relative w-11">
                            <div className="absolute -right-3 bottom-4">
                              {message.failedReason &&
                                !message.readAt &&
                                !message.deliveredAt && (
                                  <TooltipTailwind
                                    size="4"
                                    color="red-600"
                                    content={<div>{message.failedReason}</div>}
                                  />
                                )}
                            </div>
                            <Badge
                              color={
                                messageStatus === "sent"
                                  ? "violet"
                                  : messageStatus === "delivered"
                                    ? "cyan"
                                    : messageStatus === "read"
                                      ? "lime"
                                      : "rose"
                              }
                            >
                              {messageStatus}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {message &&
                          message.conversation &&
                          message.conversation.contact
                            ? `${
                                message.conversation.contact?.shopifyCustomer
                                  ?.firstName ??
                                message.conversation.contact?.firstName ??
                                message.conversation.contact?.whatsappName ??
                                "-"
                              } ${
                                message.conversation.contact?.shopifyCustomer
                                  ?.lastName ??
                                message.conversation.contact?.lastName ??
                                ""
                              }`
                            : null}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {message?.conversation?.id ? (
                              <Link
                                to={`/conversations/${message.conversation.id}`}
                                className="text-gray-600 hover:text-gray-800 hover:underline"
                              >
                                {"+" +
                                  (message?.conversation?.contact
                                    ?.userPhoneNumber ?? "")}
                              </Link>
                            ) : (
                              "+" +
                              (message?.conversation?.contact
                                ?.userPhoneNumber ?? "")
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No recipient
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!data?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Text>{pageNumber}</Text>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!data?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      </div>
    </>
  );
}
